import { Editor, Frame, SerializedNodes, useEditor } from '@craftjs/core';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BlockEditorProvider } from '@/components/blockEditor/BlockEditor.context';
import { CommonData } from '@/components/blockEditor/blockEditor.types';
import { BlockEditorSidebar } from '@/components/blockEditor/BlockEditorSidebar/BlockEditorSidebar';
import { EDUCATION_SLIDE_RESOLVER } from '@/components/blockEditor/presentation/educationSlideResolver';
import { transformCommonDataToCraft } from '@/components/blockEditor/transformCommonDataToCraft';
import { Button } from '@/components/form/baseInputs/Button';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Modal } from '@/components/modals/Modal/Modal';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { PopperContent } from '@/components/poppers/PopperContent';
import { InteractionParadigmContext } from '@/contexts/InteractionParadigm.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { EducationSlideContentWrapper } from '@/modules/presentation/clientPresentation/components/slides/EducationalSlide/EducationalSlideWrapper';
import { PresentationSlideWrapper } from '@/modules/presentation/clientPresentation/PresentationSlideWrapper';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import {
  CreateTenantSlideConfigInput,
  TenantSlideConfigConfigType,
} from '@/types/schema';

import {
  ABOUT_ESTATE_PLANNING_ITEMS,
  EDUCATION_CATEGORIES,
  EducationCategory,
  EducationListItemType,
  EducationSlideType,
  PLANNING_BASICS_CATEGORY,
  PLANNING_STRATEGIES_CATEGORY,
  PLANNING_STRATEGIES_ITEMS,
  PlanningStrategiesSlideType,
  STATE_INHERITANCE_TAX_CATEGORY,
  STATE_TAX_ITEMS,
  StateTaxEducationSlideType,
  TAX_TOPICS_CATEGORY,
  TAX_TOPICS_ITEMS,
} from '../Education.constants';
import { getCustomSlideEducationCategory } from '../Education.utils';
import { EducationSlideEditProvider } from '../EducationSlideEditContext';
import {
  isEducationPageOrganizationManaged,
  useEducationMap,
  useEducationSlideOverrides,
  useOwnedEducationSlideOverrides,
} from '../EducationSlideOverrides/EducationSlideOverrides.context';
import { InternalSlideMenu } from './components/InternalSlideMenu';
import { SaveButton } from './components/SaveButton';
import { SettingsPanel } from './components/SettingsPanel';
import { SlidePaginationControls } from './components/SlidePaginationControls';
import { UndoRedoButtons } from './components/UndoRedoButtons';
import { getEducationSlideDefaultData } from './EducationSlideDefaultData';
import {
  PageSettingsData,
  useHasUnsavedChanges,
} from './hooks/useHasUnsavedChanges';
import { useSelectedEditableNode } from './hooks/useSelectedEditableNode';
import {
  DRAFT_SLIDE_ID,
  getSlideIdentifierFromEducationSlideId,
  isCustomSlideIdentifier,
} from './utils/slideIdentifier.utils';

const EDUCATION_CATEGORY_OPTIONS: {
  display: string;
  value: EducationCategory;
}[] = EDUCATION_CATEGORIES.map((category) => ({
  display: category,
  value: category,
}));

/** Default category for new custom pages */
const DEFAULT_CUSTOM_PAGE_CATEGORY: EducationCategory =
  PLANNING_BASICS_CATEGORY;

/**
 * Gets the category for a template (non-custom) education slide.
 */
function getTemplateSlideCategoryFromEducationSlideId(
  slideId: EducationListItemType
): EducationCategory | undefined {
  if (ABOUT_ESTATE_PLANNING_ITEMS.includes(slideId as EducationSlideType)) {
    return PLANNING_BASICS_CATEGORY;
  }
  if (TAX_TOPICS_ITEMS.includes(slideId as EducationSlideType)) {
    return TAX_TOPICS_CATEGORY;
  }
  if (
    PLANNING_STRATEGIES_ITEMS.includes(slideId as PlanningStrategiesSlideType)
  ) {
    return PLANNING_STRATEGIES_CATEGORY;
  }
  if (STATE_TAX_ITEMS.includes(slideId as StateTaxEducationSlideType)) {
    return STATE_INHERITANCE_TAX_CATEGORY;
  }
  return undefined;
}

type LayoutType = '1-column' | '2-column';

const LAYOUT_OPTIONS: {
  display: string;
  value: LayoutType;
}[] = [
  { display: '1 column', value: '1-column' },
  { display: '2 columns', value: '2-column' },
];

/** Loading state shown while fetching slide configuration */
function LoadingState() {
  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '100vh',
        width: '100vw',
      }}
    >
      <CircularProgress />
      <Typography variant="body-s" sx={{ mt: 2 }}>
        Loading slide...
      </Typography>
    </Stack>
  );
}

function ErrorState({ onClose }: { onClose: () => void }) {
  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ height: '100vh', width: '100vw' }}
    >
      <Typography variant="title-xl">Something went wrong</Typography>
      <Typography variant="body-s">
        The Education page could not be loaded. Please try again later.
      </Typography>
      <Button variant="secondary" size="sm" onClick={onClose}>
        Close
      </Button>
    </Stack>
  );
}

const HEADER_HEIGHT = '73px';

export interface EducationSlideTrowserProps {
  isOpen: boolean;
  onClose: () => void;
  educationSlideId: EducationListItemType | null;
  /** Called when a new custom slide is created from a draft */
  onSlideCreated?: (slideIdentifier: string) => void;
  /** Page index to open on (multi-page slides). Defaults to the first page. */
  initialSlideIndex?: number;
}

type PendingAction =
  | { type: 'close' }
  | { type: 'navigate'; direction: 'previous' | 'next' };

interface EducationSlideTrowserEditorContentProps {
  educationSlideId: EducationListItemType;
  onClose: () => void;
  currentSlideIndex: number;
  setCurrentSlideIndex: React.Dispatch<React.SetStateAction<number>>;
  existingConfigId: string | undefined;
  setExistingConfigId: (id: string | undefined) => void;
  craftData: SerializedNodes;
  savedConfig: CreateTenantSlideConfigInput | undefined;
  isCustomPage?: boolean;
  customSlideDisplayName?: string;
  customSlideEducationCategory?: string;
  /** Display name override for template (non-custom) slides */
  templateSlideDisplayName?: string;
  onSlideCreated?: (slideIdentifier: string) => void;
  /** Config ID for page 0 (used for title storage - titles are always stored on page 0) */
  page0ConfigId?: string;
  /** Slide identifier of the source slide this was duplicated from (persisted through saves) */
  duplicatedFromSlideId?: string;
  /** Whether organization management makes the effective page read-only. */
  isReadOnly: boolean;
}

/**
 * Inner content component that requires Editor context.
 * This component uses useEditor hooks and must be rendered inside <Editor>.
 */
function EducationSlideTrowserEditorContent({
  educationSlideId,
  onClose,
  currentSlideIndex,
  setCurrentSlideIndex,
  existingConfigId,
  setExistingConfigId,
  craftData,
  savedConfig,
  isCustomPage = false,
  customSlideDisplayName,
  customSlideEducationCategory,
  templateSlideDisplayName,
  onSlideCreated,
  duplicatedFromSlideId,
  page0ConfigId,
  isReadOnly,
}: EducationSlideTrowserEditorContentProps) {
  const educationMap = useEducationMap();
  const educationSlide = educationMap[educationSlideId];
  // For custom pages, use editable title state; for template slides, use the static title or override
  const templateTitle = educationSlide?.title || '';
  const [customTitle, setCustomTitle] = useState(
    customSlideDisplayName || 'New education page'
  );
  // For template slides, allow title override (use saved displayName if available, otherwise default)
  const [templateTitleOverride, setTemplateTitleOverride] = useState(
    templateSlideDisplayName || templateTitle
  );
  const title = isCustomPage ? customTitle : templateTitleOverride;

  // For custom pages, track the education category
  const [customCategory, setCustomCategory] = useState<EducationCategory>(
    (customSlideEducationCategory as EducationCategory) ||
      DEFAULT_CUSTOM_PAGE_CATEGORY
  );

  // Get the category for template slides (non-editable)
  const templateSlideCategory =
    getTemplateSlideCategoryFromEducationSlideId(educationSlideId);

  // Settings modal state (used for both custom pages and template slides)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<LayoutType>('2-column');

  // Access the Craft.js editor to update layout
  const { actions: editorActions, query: editorQuery } = useEditor();

  // Find the TwoColumnLayout node and get/update its layout
  const findTwoColumnLayoutNodeId = useCallback(() => {
    const nodes = JSON.parse(editorQuery.serialize());
    for (const [nodeId, node] of Object.entries(nodes)) {
      const nodeData = node as {
        type?: { resolvedName?: string };
        props?: Record<string, unknown>;
      };
      if (nodeData.type?.resolvedName === 'TwoColumnLayout') {
        return nodeId;
      }
    }
    return null;
  }, [editorQuery]);

  // Sync layout state when modal opens (for custom pages)
  const handleOpenSettingsModal = useCallback(() => {
    // Only sync layout state for custom pages (template slides don't have layout option)
    if (isCustomPage) {
      const nodeId = findTwoColumnLayoutNodeId();
      if (nodeId) {
        const node = editorQuery.node(nodeId).get();
        const leftWidth = node.data.props?.leftWidth as string;
        // If left column is 100%, it's a 1-column layout
        if (leftWidth === '100%' || leftWidth === '0%') {
          setSelectedLayout('1-column');
        } else {
          setSelectedLayout('2-column');
        }
      }
    }
    setIsSettingsModalOpen(true);
  }, [isCustomPage, findTwoColumnLayoutNodeId, editorQuery]);

  // Update the TwoColumnLayout node when layout changes
  const handleLayoutChange = useCallback(
    (newLayout: LayoutType) => {
      setSelectedLayout(newLayout);
      const nodeId = findTwoColumnLayoutNodeId();
      if (nodeId) {
        // When switching to 1-column, remove items from the right column
        if (newLayout === '1-column') {
          const layoutNode = editorQuery.node(nodeId).get();
          const rightColumnId = layoutNode.data.linkedNodes?.rightColumn;
          if (rightColumnId) {
            const rightColumnNode = editorQuery.node(rightColumnId).get();
            const childNodeIds = rightColumnNode.data.nodes || [];
            // Delete each child node in the right column
            childNodeIds.forEach((childId: string) => {
              editorActions.delete(childId);
            });
          }
        }

        editorActions.setProp(nodeId, (props: Record<string, unknown>) => {
          if (newLayout === '1-column') {
            props.leftWidth = '100%';
            props.rightWidth = '0%';
            props.columnGap = 0;
          } else {
            props.leftWidth = '50%';
            props.rightWidth = '50%';
            props.columnGap = 5;
          }
        });
      }
    },
    [findTwoColumnLayoutNodeId, editorActions, editorQuery]
  );

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  // These hooks use useEditor which requires being inside the Editor context
  const { selected } = useSelectedEditableNode();

  // Track page settings for unsaved changes detection
  const currentPageSettings: PageSettingsData = useMemo(
    () => ({
      title: isCustomPage ? customTitle : templateTitleOverride,
      category: isCustomPage ? customCategory : undefined,
    }),
    [isCustomPage, customTitle, templateTitleOverride, customCategory]
  );

  // Initial page settings are captured when the component mounts
  // For custom pages: use the saved displayName or default
  // For template slides: use the saved displayName override or the default template title
  const initialPageSettings: PageSettingsData = useMemo(() => {
    if (isCustomPage) {
      return {
        title: customSlideDisplayName || 'New education page',
        category: customSlideEducationCategory || DEFAULT_CUSTOM_PAGE_CATEGORY,
      };
    }
    return {
      title: templateSlideDisplayName || templateTitle,
      category: undefined,
    };
    // Only recalculate when the slide changes, not when user edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex, isCustomPage]);

  const { checkForChanges, markAsSaved } = useHasUnsavedChanges(
    currentSlideIndex,
    currentPageSettings,
    initialPageSettings
  );

  const handleCloseAttempt = useCallback(() => {
    if (checkForChanges()) {
      setPendingAction({ type: 'close' });
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  }, [checkForChanges, onClose]);

  // Handle Escape key to check dirty state before closing
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseAttempt]);

  const handleConfirmUnsavedChanges = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setShowUnsavedChangesModal(false);

      if (reason === 'cancel' && pendingAction) {
        // User chose to leave/discard changes - execute the pending action
        if (pendingAction.type === 'close') {
          onClose();
        } else if (pendingAction.type === 'navigate') {
          if (pendingAction.direction === 'previous') {
            setCurrentSlideIndex((idx) => Math.max(idx - 1, 0));
          } else {
            const slides = educationMap[educationSlideId]?.slides ?? [];
            const lastSlideIndex = Math.max(slides.length - 1, 0);
            setCurrentSlideIndex((idx) => Math.min(idx + 1, lastSlideIndex));
          }
        }
      }
      // If 'confirm', user chose to stay - do nothing
      setPendingAction(null);
    },
    [
      onClose,
      pendingAction,
      educationSlideId,
      setCurrentSlideIndex,
      educationMap,
    ]
  );

  const handleSaveSuccess = useCallback(
    (configId: string) => {
      setExistingConfigId(configId);
      // Use setTimeout to ensure markAsSaved runs after React has processed
      // all state updates (including any triggered by onSlideCreated)
      setTimeout(() => {
        markAsSaved();
        onClose();
      }, 0);
    },
    [markAsSaved, onClose, setExistingConfigId]
  );

  // Custom pages always have 1 slide; template slides use their defined count
  const slides = isCustomPage ? [null] : (educationSlide?.slides ?? []);
  const totalSlides = slides.length;
  const slideHeading = currentSlideIndex > 0 ? `${title} (continued)` : title;

  const isSettingsPanelOpen = Boolean(selected);
  const hasPrevious = currentSlideIndex > 0;
  const hasNext = currentSlideIndex < totalSlides - 1;
  const isNavigationDisabled = isSettingsPanelOpen || totalSlides <= 1;
  const showChevronNavigation = !isSettingsPanelOpen && totalSlides > 1;

  const handlePreviousSlide = useCallback(() => {
    if (isNavigationDisabled || !hasPrevious) {
      return;
    }
    if (checkForChanges()) {
      setPendingAction({ type: 'navigate', direction: 'previous' });
      setShowUnsavedChangesModal(true);
    } else {
      setCurrentSlideIndex((idx) => Math.max(idx - 1, 0));
    }
  }, [
    isNavigationDisabled,
    hasPrevious,
    checkForChanges,
    setCurrentSlideIndex,
  ]);

  const handleNextSlide = useCallback(() => {
    if (isNavigationDisabled || !hasNext) {
      return;
    }
    if (checkForChanges()) {
      setPendingAction({ type: 'navigate', direction: 'next' });
      setShowUnsavedChangesModal(true);
    } else {
      const lastSlideIndex = Math.max(totalSlides - 1, 0);
      setCurrentSlideIndex((idx) => Math.min(idx + 1, lastSlideIndex));
    }
  }, [
    isNavigationDisabled,
    hasNext,
    checkForChanges,
    totalSlides,
    setCurrentSlideIndex,
  ]);

  const currentSlideId = `${educationSlideId}-editable-${currentSlideIndex}`;
  const hasMorePages = currentSlideIndex < totalSlides - 1;

  const slideMarkup = (
    <PresentationSlideWrapper key={currentSlideId}>
      <PresentationSlide.Slide
        id={currentSlideId}
        leftHeaderContent={
          <PresentationSlide.MainHeading heading={slideHeading} />
        }
        footer={<PresentationSlide.Footer />}
      >
        <EducationSlideContentWrapper hasMorePages={hasMorePages}>
          <Frame data={craftData} />
        </EducationSlideContentWrapper>
      </PresentationSlide.Slide>
    </PresentationSlideWrapper>
  );

  return (
    <Stack
      direction="column"
      sx={{
        height: '100vh',
        width: '100vw',
        overflowX: 'hidden',
        overflowY: 'hidden',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
          px: 2,
          py: 2,
        }}
        height={HEADER_HEIGHT}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <EditableHeader
            value={title}
            onChange={isCustomPage ? setCustomTitle : setTemplateTitleOverride}
            onInput={isCustomPage ? setCustomTitle : setTemplateTitleOverride}
            disabled={isReadOnly}
            showPencilIcon={!isReadOnly}
            typographyProps={{ component: 'h1' }}
          />
          {isReadOnly ? (
            <Badge
              variant={BadgeVariants.SecondaryFilled}
              display="Organization managed"
            />
          ) : null}
          <SlidePaginationControls
            currentIndex={currentSlideIndex}
            totalSlides={totalSlides}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {!isReadOnly && <UndoRedoButtons />}
          <Button
            variant="secondary"
            size="sm"
            onClick={isReadOnly ? onClose : handleCloseAttempt}
            data-testid="close-education-slide-trowser"
          >
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <SaveButton
              educationSlideId={educationSlideId}
              currentSlideIndex={currentSlideIndex}
              existingConfigId={existingConfigId}
              onSaveSuccess={handleSaveSuccess}
              onSlideCreated={onSlideCreated}
              isCustomPage={isCustomPage}
              customSlideEducationCategory={
                isCustomPage ? customCategory : undefined
              }
              customSlideTitle={isCustomPage ? customTitle : undefined}
              duplicatedFromSlideId={duplicatedFromSlideId}
              templateSlideTitle={
                !isCustomPage ? templateTitleOverride : undefined
              }
              page0ConfigId={page0ConfigId}
            />
          )}
          <InternalSlideMenu
            config={savedConfig}
            onOpenSettings={isReadOnly ? undefined : handleOpenSettingsModal}
          />
        </Stack>
      </Stack>
      <BlockEditorProvider isEditMode={!isReadOnly} rootNodeId="ROOT">
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {!isReadOnly && <BlockEditorSidebar />}
          <Box
            flex={1}
            sx={{
              minWidth: 0,
              backgroundColor: COLORS.GRAY[50],
              position: 'relative',
            }}
          >
            <Stack direction="row" height="100%" alignItems="stretch">
              {showChevronNavigation && (
                <Stack
                  alignItems="flex-end"
                  justifyContent="center"
                  sx={{ width: 80, pr: 2, flexShrink: 0, zIndex: 1 }}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Previous page"
                    onClick={handlePreviousSlide}
                    disabled={!hasPrevious}
                    square
                  >
                    <ChevronLeftIcon size={16} />
                  </Button>
                </Stack>
              )}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'scroll',
                  py: 2,
                }}
              >
                <Stack
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <InteractionParadigmContext.Provider
                    value={{ viewOnly: true }}
                  >
                    <CustomerThemeProvider>{slideMarkup}</CustomerThemeProvider>
                  </InteractionParadigmContext.Provider>
                </Stack>
              </Box>
              {showChevronNavigation && (
                <Stack
                  alignItems="flex-start"
                  justifyContent="center"
                  sx={{ width: 80, pl: 2, flexShrink: 0, zIndex: 1 }}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Next page"
                    onClick={handleNextSlide}
                    disabled={!hasNext}
                    square
                  >
                    <ChevronRightIcon size={16} />
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
          {!isReadOnly && <SettingsPanel />}

          {/* Unsaved changes confirmation modal */}
          <ConfirmationModal
            isOpen={!isReadOnly && showUnsavedChangesModal}
            heading="Unsaved changes"
            cancelText="Leave"
            confirmText="Stay"
            cancelButtonProps={{ variant: 'destructive-primary' }}
            confirmButtonProps={{ variant: 'secondary' }}
            closeOnOutsideClick={false}
            onClose={handleConfirmUnsavedChanges}
          >
            You have unsaved changes. Are you sure you want to continue? Your
            changes will not be saved.
          </ConfirmationModal>

          {/* Settings modal for both custom pages and template slides */}
          <Modal
            isOpen={!isReadOnly && isSettingsModalOpen}
            onClose={() => setIsSettingsModalOpen(false)}
            heading="Page settings"
            rightHeaderContent={null}
            actions={
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsSettingsModalOpen(false)}
              >
                Done
              </Button>
            }
          >
            <Stack spacing={3}>
              <SelectInput
                label="Category"
                name="educationCategory"
                value={
                  isCustomPage ? customCategory : templateSlideCategory || ''
                }
                options={EDUCATION_CATEGORY_OPTIONS}
                showEmptyValue={false}
                disabled={!isCustomPage}
                onChange={(e) =>
                  setCustomCategory(e.target.value as EducationCategory)
                }
                contextualHelp={
                  !isCustomPage ? (
                    <PopperContent body="The category is determined by the slide type and cannot be changed. Create a custom page if you need a different category." />
                  ) : undefined
                }
              />
              {isCustomPage && (
                <SelectInput
                  label="Layout"
                  name="layout"
                  value={selectedLayout}
                  options={LAYOUT_OPTIONS}
                  showEmptyValue={false}
                  onChange={(e) =>
                    handleLayoutChange(e.target.value as LayoutType)
                  }
                  helpText="Changing from 2 columns to 1 column will remove content in the second column"
                />
              )}
            </Stack>
          </Modal>
        </Box>
      </BlockEditorProvider>
    </Stack>
  );
}

interface EducationSlideTrowserContentProps {
  educationSlideId: EducationListItemType;
  onClose: () => void;
  onSlideCreated?: (slideIdentifier: string) => void;
  /** Page index to open on (multi-page slides). Defaults to the first page. */
  initialSlideIndex?: number;
}

/**
 * Outer content component that handles data fetching BEFORE rendering the Editor.
 * This ensures we have the correct initial data for the Frame.
 */
function EducationSlideTrowserContent({
  educationSlideId,
  onClose,
  onSlideCreated,
  initialSlideIndex = 0,
}: EducationSlideTrowserContentProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);

  // Track the config ID for the current slide/page (may be set after save)
  const [existingConfigId, setExistingConfigId] = useState<string | undefined>(
    undefined
  );

  // Check if this is a custom slide (DRAFT_SLIDE_ID for new, or existing custom slide identifier)
  const isDraft = educationSlideId === DRAFT_SLIDE_ID;
  const isExistingCustomSlide = isCustomSlideIdentifier(educationSlideId);
  const isCustomPage = isDraft || isExistingCustomSlide;

  // Generate the slide identifier for the current slide/page
  const currentSlideIdentifier = useMemo(
    () =>
      getSlideIdentifierFromEducationSlideId(
        educationSlideId,
        currentSlideIndex
      ),
    [educationSlideId, currentSlideIndex]
  );

  // Generate the slide identifier for page 0 (used for title storage)
  const page0SlideIdentifier = useMemo(
    () => getSlideIdentifierFromEducationSlideId(educationSlideId, 0),
    [educationSlideId]
  );

  const {
    overridesMap,
    customSlidesMap,
    loading: configsLoading,
    error: configsError,
  } = useEducationSlideOverrides();
  const {
    ownedOverridesMap,
    loading: ownedOverridesLoading,
    error: ownedOverridesError,
  } = useOwnedEducationSlideOverrides();

  // Mutation IDs must come only from tenant-owned management rows.
  // For drafts (new custom pages), we don't look up any existing config
  const existingOverride = isDraft
    ? undefined
    : ownedOverridesMap[currentSlideIdentifier];
  const effectiveOverride = isDraft
    ? undefined
    : overridesMap[currentSlideIdentifier];
  const existingCustomSlide = isExistingCustomSlide
    ? customSlidesMap[educationSlideId]
    : undefined;
  const isOrganizationManaged = useMemo(
    () =>
      !isCustomPage &&
      isEducationPageOrganizationManaged(educationSlideId, overridesMap),
    [educationSlideId, isCustomPage, overridesMap]
  );

  // Get page 0's override (used for title storage - titles are always stored on page 0)
  const page0Override = isDraft
    ? undefined
    : ownedOverridesMap[page0SlideIdentifier];
  const effectivePage0Override = isDraft
    ? undefined
    : overridesMap[page0SlideIdentifier];

  // Update existingConfigId when existing config changes
  useEffect(() => {
    // Drafts are always new - never have an existing config
    if (isDraft) {
      setExistingConfigId(undefined);
      return;
    }

    if (isExistingCustomSlide && existingCustomSlide?.id) {
      setExistingConfigId(existingCustomSlide.id);
    } else if (existingOverride?.id) {
      setExistingConfigId(existingOverride.id);
    } else {
      // Query completed but no config found
      setExistingConfigId(undefined);
    }
  }, [existingOverride, existingCustomSlide, isExistingCustomSlide, isDraft]);

  useEffect(() => {
    setCurrentSlideIndex(initialSlideIndex);
  }, [educationSlideId, initialSlideIndex]);

  const defaultData = useMemo(
    () =>
      // For custom slides (draft or existing), use DRAFT default data as fallback
      // For template slides, use the specific slide's default
      isCustomPage
        ? getEducationSlideDefaultData(DRAFT_SLIDE_ID, 0)
        : getEducationSlideDefaultData(educationSlideId, currentSlideIndex),
    [educationSlideId, currentSlideIndex, isCustomPage]
  );
  const existingCustomSlideContent = existingCustomSlide?.contentJSON;
  const templateOverride = isOrganizationManaged
    ? effectiveOverride
    : (existingOverride ?? effectiveOverride);

  let savedConfig: CreateTenantSlideConfigInput | undefined;
  if (existingCustomSlide) {
    savedConfig = {
      ...existingCustomSlide,
      configType: TenantSlideConfigConfigType.CustomSlideTemplate,
    };
  } else if (!isCustomPage && templateOverride) {
    savedConfig = {
      ...templateOverride,
      slideIdentifier: currentSlideIdentifier,
      configType: TenantSlideConfigConfigType.EducationSlideOverride,
    };
  }

  // Determine the data to use for the Frame
  // Wait for query to complete before deciding
  const commonData = useMemo(() => {
    if (configsLoading || ownedOverridesLoading) return null;

    // For existing custom slides, load from customSlidesMap
    if (isExistingCustomSlide && existingCustomSlideContent) {
      const savedCommonData = existingCustomSlideContent as CommonData;
      if (savedCommonData.blocks && Array.isArray(savedCommonData.blocks)) {
        return savedCommonData;
      }
    }

    // Prefer an owned draft for editing. Otherwise, use effective content as the
    // starting point without treating the effective row as a mutation target.
    if (!isCustomPage && templateOverride?.contentJSON) {
      const savedCommonData = templateOverride.contentJSON as CommonData;
      if (savedCommonData.blocks && Array.isArray(savedCommonData.blocks)) {
        return savedCommonData;
      }
    }

    // Fall back to default data
    if (!defaultData) return null;
    return defaultData;
  }, [
    configsLoading,
    ownedOverridesLoading,
    isExistingCustomSlide,
    existingCustomSlideContent,
    templateOverride,
    isCustomPage,
    defaultData,
  ]);

  const craftData = useMemo(
    () =>
      commonData
        ? transformCommonDataToCraft(
            commonData,
            'ROOT',
            'SlideRoot',
            {
              slideId: educationSlideId,
              slideIndex: currentSlideIndex,
            },
            true // useLinkedNodesForRootChildren
          )
        : null,
    [commonData, educationSlideId, currentSlideIndex]
  );

  if (configsError || ownedOverridesError) {
    return <ErrorState onClose={onClose} />;
  }

  // Show loading state while fetching config data
  if (configsLoading || ownedOverridesLoading) {
    return <LoadingState />;
  }

  if (!craftData) {
    return <LoadingState />;
  }

  // Once data is ready, render the Editor with the correct initial data
  return (
    <Editor
      resolver={EDUCATION_SLIDE_RESOLVER}
      enabled={!isOrganizationManaged}
    >
      <EducationSlideEditProvider
        isEditMode={!isOrganizationManaged}
        isCustomPage={isCustomPage}
      >
        <EducationSlideTrowserEditorContent
          educationSlideId={educationSlideId}
          onClose={onClose}
          currentSlideIndex={currentSlideIndex}
          setCurrentSlideIndex={setCurrentSlideIndex}
          existingConfigId={existingConfigId}
          setExistingConfigId={setExistingConfigId}
          craftData={craftData}
          savedConfig={savedConfig}
          isCustomPage={isCustomPage}
          customSlideDisplayName={existingCustomSlide?.displayName ?? undefined}
          customSlideEducationCategory={
            existingCustomSlide
              ? getCustomSlideEducationCategory(existingCustomSlide)
              : undefined
          }
          duplicatedFromSlideId={(() => {
            if (!existingCustomSlide?.contentJSON) return undefined;
            const cj = existingCustomSlide.contentJSON as unknown as Record<
              string,
              unknown
            >;
            return typeof cj.duplicatedFrom === 'string'
              ? cj.duplicatedFrom
              : undefined;
          })()}
          templateSlideDisplayName={
            (isOrganizationManaged ? effectivePage0Override : page0Override)
              ?.displayName ?? undefined
          }
          onSlideCreated={onSlideCreated}
          page0ConfigId={page0Override?.id}
          isReadOnly={isOrganizationManaged}
        />
      </EducationSlideEditProvider>
    </Editor>
  );
}

export function EducationSlideTrowser({
  isOpen,
  onClose,
  educationSlideId,
  onSlideCreated,
  initialSlideIndex,
}: EducationSlideTrowserProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!educationSlideId) return null;

  return (
    <FullScreenModal
      trowserKey={TrowserKey.EducationPages}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <EducationSlideTrowserContent
        educationSlideId={educationSlideId}
        onClose={handleClose}
        onSlideCreated={onSlideCreated}
        initialSlideIndex={initialSlideIndex}
      />
    </FullScreenModal>
  );
}
